import { useAlert } from '@ads/components-react';
import { useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  getApiV1ExamRevisionGridQueryKey,
  useGetApiV1ExamRevisionPublishBotexamid,
  usePostApiV1ExamRevisionPublish,
} from '@/shared/api/generated';
import type { PublishBotExamViewModel } from '@/shared/api/generated/types/PublishBotExamViewModel';
import { getApiErrorMessage } from '@/shared/utils/getApiErrorMessage';

/**
 * Reúne o fluxo de publicação de uma prova em revisão: carrega os dados de prévia
 * via `GET /api/v1/exam/revision/publish/{botExamId}` (`PublishBotExamShowViewModel`,
 * habilitado apenas com o modal aberto) para pré-preencher o formulário, e submete
 * `POST /api/v1/exam/revision/publish` (`PublishBotExamViewModel`) ao confirmar.
 *
 * Ao publicar com sucesso, invalida o grid de revisão e dispara um alert de
 * sucesso; em falha (prévia ou publicação), dispara um alert de erro e mantém o
 * modal aberto (o consumidor só fecha no `onSuccess`).
 *
 * @param botExamId - ID da prova bot a publicar
 * @param enabled - `true` quando o modal está aberto (habilita a prévia)
 * @returns Dados da prévia, handler de publicação e estados de loading
 *
 * @example
 * ```tsx
 * const { show, isLoadingShow, publish, isPublishing } = usePublishRevision(id, isOpen);
 * publish(payload, { onSuccess: closeModal });
 * ```
 */
export function usePublishRevision(botExamId: number, enabled: boolean) {
  const queryClient = useQueryClient();
  const { alert } = useAlert();
  const { t } = useTranslation('examRevision');

  const query = useGetApiV1ExamRevisionPublishBotexamid(botExamId, {
    query: { enabled: enabled && botExamId > 0 },
  });

  useEffect(() => {
    if (query.isError) alert({ alertType: 'warning', title: t('publish.toast.showError') });
  }, [query.isError, alert, t]);

  const mutation = usePostApiV1ExamRevisionPublish({
    mutation: {
      onSuccess: () => {
        alert({ alertType: 'success', title: t('publish.toast.publishSuccess') });
        void queryClient.invalidateQueries({ queryKey: getApiV1ExamRevisionGridQueryKey() });
      },
      onError: error =>
        alert({
          alertType: 'warning',
          title: getApiErrorMessage(error, t('publish.toast.publishError')),
        }),
    },
  });

  const publish = (data: PublishBotExamViewModel, callbacks?: { onSuccess?: () => void }) => {
    mutation.mutate(
      { data },
      callbacks?.onSuccess ? { onSuccess: callbacks.onSuccess } : undefined,
    );
  };

  return {
    show: query.data ?? null,
    isLoadingShow: query.isLoading,
    isError: query.isError,
    publish,
    isPublishing: mutation.isPending,
  } as const;
}
